import React, { forwardRef, useState } from "react";
import { shallowEqual } from "react-redux";
import { useUscDispatch, useUscSelector } from "../../../../redux/store";
import { useUscLabel } from "../../../../utils/labels";
import { UscDisclaimerType } from "../../../../../shared-logic/types/UscCommonTypes";
import { formatPriceIntl } from "../../../../../../common-deprecated/priceUtils";
import DisclaimerIconWrapper from "../DisclaimerIconWrapper";
import { enableUscMonthlyRate } from "../../../../utils/settings";
import { getAccessoryPackPriceSelector, type UscPackType } from "../../../../utils/accessoryUtils";
import Card from "../../styles/molecules/Card";
import Text from "../../../../../shared-components/toyota/styles/atoms/Text";
import * as Styles from "./Packitem.styles";
import * as CardStyles from "../../styles/molecules/Card.styles";
import { trackPackShowFeaturesClick } from "../../../../utils/tracking";
import Collapse from "../../styles/molecules/Collapse";
import Button from "../../../../../shared-components/toyota/styles/atoms/Button";
import { addPack, removePack } from "../../../../redux/actions/UscBasketActions";
import { isPackInBasketSelector } from "../../../../utils/basketUtils";
import Icon from "../../../../../shared-components/toyota/styles/atoms/Icon";
import AccessoryPackMonthlyPrice from "./AccessoryMonthlyPrice";

const PackItem = forwardRef<HTMLDivElement, { pack: UscPackType; index: number }>(({ pack, index }, ref) => {
    const dispatch = useUscDispatch();
    const packPrice = useUscSelector(getAccessoryPackPriceSelector(pack), shallowEqual);
    const vehicleType = useUscSelector((state) => state.vehicle.type);
    const commonSettings = useUscSelector((state) => state.commonSettings);
    const showMonthlyPrice = useUscSelector(
        ({ commonSettings: financeCommonSettings, uscSettings, vehicle }) =>
            enableUscMonthlyRate(financeCommonSettings, uscSettings, vehicle.result, vehicle.type) &&
            !uscSettings.disableAccessoryMonthlyPrice,
    );
    const { enablePacks: allowPackToBeAdded } = useUscSelector((state) => state.uscSettings.basketConfig);
    const isPackInBasket = useUscSelector(isPackInBasketSelector(pack.id), shallowEqual);
    const [loadingLabel, buttonShowFeaturesLabel, buttonHideFeaturesLabel, buttonPackAddLabel, buttonPackRemoveLabel] =
        useUscLabel(["uscLoading", "uscPdpPacksShowFeatures", "uscPdpPacksHideFeatures", "uscPdpAdd", "uscPdpRemove"]);

    const [showFeatures, setShowFeatures] = useState<boolean>(false);

    const handleButtonClick = (): void => {
        if (isPackInBasket) dispatch(removePack(pack.id));
        else dispatch(addPack(pack));
    };

    return (
        <Card variant="pack" cardRef={ref} data-index-number={index}>
            <CardStyles.Body>
                <Text tag="h4" variant="h6">
                    {pack.name}
                </Text>
                {!!pack.items?.length && (
                    <Collapse variant="default">
                        <Button
                            variant="link"
                            size="small"
                            aria-controls="collapse-packs"
                            aria-expanded={showFeatures}
                            onClick={() => {
                                if (!showFeatures) trackPackShowFeaturesClick(vehicleType, pack.name);
                                setShowFeatures(!showFeatures);
                            }}
                        >
                            {showFeatures ? buttonHideFeaturesLabel : buttonShowFeaturesLabel}
                        </Button>
                        <Styles.AccessoriesGrid id="collapse-packs" hidden={!showFeatures} aria-hidden={!showFeatures}>
                            {pack.description && (
                                <Styles.AccessoriesDescription>{pack.description}</Styles.AccessoriesDescription>
                            )}
                            {pack.items?.map((application) => (
                                <Card variant="accessories-in-pack">
                                    {application.imageUrl && (
                                        <img src={application.imageUrl} alt="" role="presentation" />
                                    )}
                                    {application.name && (
                                        <CardStyles.Body>
                                            <Text tag="h5" variant="h6">
                                                {application.name}
                                            </Text>
                                        </CardStyles.Body>
                                    )}
                                </Card>
                            ))}
                        </Styles.AccessoriesGrid>
                    </Collapse>
                )}
            </CardStyles.Body>
            <CardStyles.Footer>
                <CardStyles.FlexBox flexDirection="column" justifyContent="center">
                    <Text tag="div" variant="body-regular">
                        {packPrice?.cashLoading || !packPrice ? (
                            loadingLabel
                        ) : (
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: packPrice?.cashPrice?.cashPriceInclVat
                                        ? formatPriceIntl(commonSettings, packPrice.cashPrice.cashPriceInclVat)
                                              .primaryPrice
                                        : "",
                                }}
                            />
                        )}
                        <DisclaimerIconWrapper type={UscDisclaimerType.Accessory} />
                    </Text>
                    {/* 
                            The monthly price for a pack will not change whenever an item is added to the basket,
                            but due to how the finance logic is set up in the reducer, it will always be set to
                            loading when the basket updates. 
                            Only show a loading indicator if the pack price is not available yet.
                            If this logic ever expands, we should consider moving this to the reducer.
                        */}
                    {showMonthlyPrice && (
                        <Text tag="div" variant="body-book" color="grey-7">
                            <AccessoryPackMonthlyPrice monthlyPrice={packPrice} />
                        </Text>
                    )}
                </CardStyles.FlexBox>
                {allowPackToBeAdded && (
                    <Button
                        variant={!isPackInBasket ? "pack" : "pack-outline"}
                        size="small"
                        onClick={handleButtonClick}
                    >
                        {!isPackInBasket ? buttonPackAddLabel : buttonPackRemoveLabel}
                        <Icon variant={!isPackInBasket ? "plus" : "close"} />
                    </Button>
                )}
            </CardStyles.Footer>
        </Card>
    );
});

export default PackItem;
